import { Controller } from "@hotwired/stimulus"

const PATTERN_SUGGESTIONS = {
  default: ".*",
  email: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
  name: "^[A-Za-zÀ-ÖØ-öø-ÿ\\s]+$",
  tel: "^\\+?[0-9()\\-\\s]{10,20}$"
}

export default class extends Controller {
  static targets = ["fieldsContainer", "servicesContainer", "fieldTemplate", "serviceTemplate"]

  connect() {
    this.uniqueSequence = 0
    this.boundHandleTypeChange = this.handleTypeChange.bind(this)
    this.boundHandleClick = this.handleDelegatedClick.bind(this)
    this.element.addEventListener("change", this.boundHandleTypeChange)
    this.element.addEventListener("click", this.boundHandleClick)
    this.fieldsContainerTarget
      .querySelectorAll("[data-dynamic-wrapper]")
      .forEach((wrapper) => {
        this.ensureOptionsUI(wrapper)
        this.toggleOptionsFor(wrapper)
        this.ensureOptionRowWhenNeeded(wrapper)
      })
  }

  disconnect() {
    if (this.boundHandleTypeChange) {
      this.element.removeEventListener("change", this.boundHandleTypeChange)
    }
    if (this.boundHandleClick) {
      this.element.removeEventListener("click", this.boundHandleClick)
    }
  }

  addField(event) {
    if (event) event.preventDefault()
    this.add(this.fieldTemplateTarget, this.fieldsContainerTarget)
  }

  addService(event) {
    if (event) event.preventDefault()
    this.add(this.serviceTemplateTarget, this.servicesContainerTarget)
  }

  add(template, container) {
    const uniqueId = this.generateUniqueId()
    const content = template.innerHTML.replace(/NEW_RECORD/g, uniqueId)
    const fragment = document.createRange().createContextualFragment(content)
    const firstElement = fragment.firstElementChild
    container.appendChild(fragment)

    if (firstElement) {
      this.ensureOptionsUI(firstElement)
      this.updatePatternSuggestion(firstElement)
      this.toggleOptionsFor(firstElement)
      this.ensureOptionRowWhenNeeded(firstElement)
    }
  }

  remove(event) {
    if (event) event.preventDefault()
    const wrapper = event.target.closest("[data-dynamic-wrapper]")
    if (!wrapper) return

    const destroyField = wrapper.querySelector("input[name*='[_destroy]']")
    if (destroyField) {
      destroyField.value = "1"
      wrapper.classList.add("hidden")
      return
    }

    wrapper.remove()
  }

  suggestPattern(event) {
    const source = event.currentTarget || event.target.closest('[data-registration-role]') || event.target
    const wrapper = source.closest("[data-dynamic-wrapper]")
    if (!wrapper) return

    this.ensureOptionsUI(wrapper)
    this.updatePatternSuggestion(wrapper)
    this.toggleOptionsFor(wrapper)
    this.ensureOptionRowWhenNeeded(wrapper)
  }

  onRegistrationTypeChanged(event) {
    const typeField = event.currentTarget
    if (!typeField) return

    const wrapper = typeField.closest("[data-dynamic-wrapper]")
    if (!wrapper) return

    this.ensureOptionsUI(wrapper)
    this.updatePatternSuggestion(wrapper)
    this.toggleOptionsFor(wrapper)
    this.ensureOptionRowWhenNeeded(wrapper)
  }

  handleTypeChange(event) {
    const typeField = event.target.closest('[data-registration-role="type"]')
    if (!typeField) return

    const wrapper = typeField.closest("[data-dynamic-wrapper]")
    if (!wrapper) return

    this.ensureOptionsUI(wrapper)
    this.updatePatternSuggestion(wrapper)
    this.toggleOptionsFor(wrapper)
    this.ensureOptionRowWhenNeeded(wrapper)
  }

  handleDelegatedClick(event) {
    const addButton = event.target.closest('[data-action~="dynamic-fields#addOption"]')
    if (addButton) {
      this.addOption(event)
      return
    }

    const removeButton = event.target.closest('[data-action~="dynamic-fields#removeOption"]')
    if (removeButton) {
      this.removeOption(event)
    }
  }

  markPatternCustom(event) {
    event.target.dataset.autoSuggested = "false"
  }

  updatePatternSuggestion(wrapper) {
    const patternInput = wrapper.querySelector('[data-registration-role="pattern"]')
    if (!patternInput) return

    const suggested = this.suggestedPatternFor(wrapper)
    const knownPatterns = Object.values(PATTERN_SUGGESTIONS)
    const currentValue = patternInput.value.trim()
    const isAutoSuggested = patternInput.dataset.autoSuggested !== "false"

    if (!isAutoSuggested && currentValue.length > 0 && !knownPatterns.includes(currentValue)) {
      return
    }

    patternInput.value = suggested
    patternInput.dataset.autoSuggested = "true"
  }

  suggestedPatternFor(wrapper) {
    const type = wrapper.querySelector('[data-registration-role="type"]')?.value || "text"
    const name = (wrapper.querySelector('[data-registration-role="name"]')?.value || "").toLowerCase()
    const label = (wrapper.querySelector('[data-registration-role="label"]')?.value || "").toLowerCase()

    if (type === "email") return PATTERN_SUGGESTIONS.email
    if (type === "tel") return PATTERN_SUGGESTIONS.tel
    if (type === "option" || type === "checkbox" || type === "radio") return PATTERN_SUGGESTIONS.default
    if (type === "text" && (name.includes("nome") || label.includes("nome"))) return PATTERN_SUGGESTIONS.name

    return PATTERN_SUGGESTIONS.default
  }

  toggleOptionsFor(wrapper) {
    const type = wrapper.querySelector('[data-registration-role="type"]')?.value || "text"
    const optionsContainer = this.ensureOptionsUI(wrapper)
    const optionFields = wrapper.querySelectorAll('[data-registration-role="option-field"]')

    if (!optionsContainer) return

    const needsOptions = type === "option" || type === "checkbox" || type === "radio"
    optionsContainer.classList.toggle("hidden", !needsOptions)
    optionFields.forEach((field) => {
      field.disabled = !needsOptions
      field.required = needsOptions
    })
  }

  addOption(event) {
    if (event) event.preventDefault()
    const trigger = event?.currentTarget || event?.target
    const wrapper = trigger?.closest("[data-dynamic-wrapper]")
    if (!wrapper) return

    this.addOptionRow(wrapper)
    this.toggleOptionsFor(wrapper)
  }

  removeOption(event) {
    if (event) event.preventDefault()
    const trigger = event?.currentTarget || event?.target
    const row = trigger?.closest("[data-option-row]")
    if (row) row.remove()
  }

  ensureOptionRowWhenNeeded(wrapper) {
    const type = wrapper.querySelector('[data-registration-role="type"]')?.value || "text"
    if (!(type === "option" || type === "checkbox" || type === "radio")) return

    this.ensureOptionsUI(wrapper)
    const list = wrapper.querySelector('[data-registration-role="options-list"]')
    if (!list) return
    if (list.querySelector("[data-option-row]")) return

    this.addOptionRow(wrapper)
    this.toggleOptionsFor(wrapper)
  }

  addOptionRow(wrapper) {
    this.ensureOptionsUI(wrapper)
    const list = wrapper.querySelector('[data-registration-role="options-list"]')
    if (!list) return

    const optionId = this.generateUniqueId()
    const typeFieldName = wrapper.querySelector('[data-registration-role="type"]')?.name || ""
    const baseName = typeFieldName.replace(/\[type\]$/, "[options][NEW_OPTION]")
    if (!baseName) return

    let content = `
      <div class="grid grid-cols-[1fr_1fr_auto] gap-2" data-option-row>
        <input
          type="text"
          name="${baseName}[text]"
          placeholder="Texto exibido"
          class="w-full rounded border-slate-300 text-xs"
          data-registration-role="option-field">
        <input
          type="text"
          name="${baseName}[value]"
          placeholder="Valor enviado"
          class="w-full rounded border-slate-300 text-xs"
          data-registration-role="option-field">
        <button type="button" data-action="dynamic-fields#removeOption" class="rounded border border-slate-300 px-2 text-xs text-slate-500 hover:text-red-500">×</button>
      </div>
    `

    content = content.replace(/NEW_OPTION/g, optionId)
    const fragment = document.createRange().createContextualFragment(content)
    list.appendChild(fragment)
  }

  generateUniqueId() {
    this.uniqueSequence += 1
    // Keep numeric-only keys for Rails nested attributes strong params parsing.
    return `${Date.now()}${this.uniqueSequence}`
  }

  ensureOptionsUI(wrapper) {
    let optionsContainer = wrapper.querySelector('[data-registration-role="options-container"]')
    if (optionsContainer) return optionsContainer

    const patternField = wrapper.querySelector('[data-registration-role="pattern"]')?.closest("div")
    if (!patternField) return null

    optionsContainer = document.createElement("div")
    optionsContainer.className = "col-span-2 space-y-2 hidden"
    optionsContainer.dataset.registrationRole = "options-container"
    optionsContainer.innerHTML = `
      <div class="flex items-center justify-between">
        <label class="block text-[10px] font-bold text-slate-400 uppercase">Opções (text/value)</label>
        <button type="button" data-action="dynamic-fields#addOption" class="text-[10px] font-bold text-dsop-green-action hover:underline">+ Opção</button>
      </div>
      <p class="text-[10px] text-slate-500">Use para tipos Lista (select), Checkbox (1 opção) e Radio (1 opção).</p>
      <div class="space-y-2" data-registration-role="options-list"></div>
    `

    patternField.insertAdjacentElement("afterend", optionsContainer)
    return optionsContainer
  }
}
